import "../philosophy.css";
import { tokenizeHtml } from "../hl";
import { revealHostProps } from "../reveal-host";
import Closing from "./Closing";
import HtmlLogo from "./HtmlLogo";
import Nav from "./Nav";
import ReactLogo from "./ReactLogo";
import SolidLogo from "./SolidLogo";
import SvelteLogo from "./SvelteLogo";
import TypeIn from "./TypeIn";

const COUNTER_SRC = `
function Counter() {
  const [count, setCount] = useState(0);
  return <button onClick={() => setCount(count + 1)}>{count}</button>;
}
`;

const EXPORT_SRC = `
$ npx jetshake export dialog.tsx
✓ jet-dialog.js  4.9 KB gz - self-contained, no react-dom

<!-- any page, any stack -->
<script type="module" src="/js/jet-dialog.js"></script>
<jet-dialog title="Delete project?"></jet-dialog>
`;

/**
 * Static figures built as HTML strings (like TypeIn) - a JSX `.map()` would
 * pull the keyed-list differ into the shared runtime chunk the landing loads.
 */

/** Payload ladder - widths are % of the React baseline (45 KB gz). */
const LADDER: Array<[string, string, number, boolean]> = [
  ["Handwritten floor", "~0.5 KB", 1.2, false],
  ["Compiled counter", "~2.5 KB", 5.6, false],
  ["This site (landing, total)", "~8 KB", 17.8, false],
  ["React runtime, before your code", "~45 KB", 100, true],
];

const LADDER_HTML = LADDER.map(
  ([label, kb, pct, react]) =>
    '<div class="phil-ladder-row">' +
    `<span class="phil-ladder-label">${label}</span>` +
    '<span class="phil-ladder-track">' +
    `<span class="phil-ladder-bar${react ? " phil-ladder-bar--react" : ""}" style="width:${pct}%"></span>` +
    "</span>" +
    `<span class="phil-ladder-kb">${kb}</span>` +
    "</div>",
).join("");

const MOONS: Array<[string, boolean]> = [
  ["signal-core", true],
  ["append", true],
  ["keyed-list", false],
  ["styles", false],
  ["props", false],
  ["refs", false],
  ["context", false],
  ["transitions", false],
  ["checked-attrs", false],
  ["event-delegation", false],
  ["external-store", false],
  ["use-id", false],
  ["hydrate", false],
  ["tracking", false],
];

const MOONS_HTML = MOONS.map(
  ([name, on]) =>
    `<span class="phil-moon${on ? " phil-moon--on" : ""}">${name}</span>`,
).join("");

/** Mixed tree - per-component bail-out, everything around it compiled. */
const TREE: Array<[string, string, boolean]> = [
  ["", "App", false],
  ["├─", "Nav", false],
  ["├─", "Dashboard", false],
  ["│&nbsp;&nbsp;&nbsp;├─", "Chart", false],
  ["│&nbsp;&nbsp;&nbsp;└─", "LegacyTable", true],
  ["└─", "Footer", false],
];

const TREE_HTML = TREE.map(
  ([branch, name, fallback]) =>
    '<div class="phil-tree-row">' +
    `<span class="phil-tree-branch">${branch}</span>` +
    `<span class="phil-node${fallback ? " phil-node--fallback" : ""}">${name}</span>` +
    (fallback
      ? '<span class="phil-node-note">fallback - correct, just bigger</span>'
      : "") +
    "</div>",
).join("");

/** First-content timeline - where the page appears in each delivery model. */
const TIMELINE_HTML =
  '<div class="phil-tl-row">' +
  '<span class="phil-tl-label">Classic SPA</span>' +
  '<span class="phil-tl-track">' +
  '<span class="phil-tl-seg phil-tl-seg--empty" style="width:16%">empty HTML</span>' +
  '<span class="phil-tl-seg phil-tl-seg--heavy" style="width:54%">runtime + app JS boots</span>' +
  '<span class="phil-tl-seg phil-tl-seg--paint" style="width:28%">first content</span>' +
  "</span>" +
  "</div>" +
  '<div class="phil-tl-row">' +
  '<span class="phil-tl-label">JetShake</span>' +
  '<span class="phil-tl-track">' +
  '<span class="phil-tl-seg phil-tl-seg--paint" style="width:34%">the page, as HTML</span>' +
  '<span class="phil-tl-seg phil-tl-seg--lean" style="width:14%">8 KB JS</span>' +
  '<span class="phil-tl-seg phil-tl-seg--idle" style="width:50%"></span>' +
  "</span>" +
  "</div>";

/** This site's build report, as printed by `vite build` (trimmed). */
const REPORT_HTML = [
  '<span class="phil-report-dim">[jetshake] Build report</span>',
  'packaging shape : <span class="tok-ok">app</span>',
  'delivery intent : <span class="tok-ok">html-first</span>',
  'leanBoot        : <span class="tok-ok">ON</span>',
  '33 compiled, <span class="tok-ok">0 bailed to compat</span>',
  '<span class="tok-ok">★ fully compiled - zero bailouts</span>',
  'compat renderer : 0.0 KB  <span class="tok-ok">none</span>',
].join("\n");

function PayloadLadder() {
  return (
    <figure className="phil-ladder" aria-label="Payload comparison">
      <div
        className="phil-ladder-rows"
        dangerouslySetInnerHTML={{ __html: LADDER_HTML }}
      />
      <figcaption className="phil-fig-note">
        Compressed over-the-wire sizes, measured. JetShake rows include HTML,
        CSS and runtime where noted; the React row is the runtime alone.
      </figcaption>
    </figure>
  );
}

function MoonGrid() {
  return (
    <figure className="phil-moons" aria-label="Runtime modules a counter ships">
      <p className="phil-fig-title">The runtime, as shipped for a counter</p>
      <div
        className="phil-moons-grid"
        dangerouslySetInnerHTML={{ __html: MOONS_HTML }}
      />
      <figcaption className="phil-fig-note">
        Each module is emitted only when the compiled output references it. A
        counter lights up two of them. A framework ships the lot regardless.
      </figcaption>
    </figure>
  );
}

function MixedTree() {
  return (
    <figure className="phil-tree" aria-label="Mixed compiled and fallback tree">
      <p className="phil-fig-title">One tree, per-component guarantee</p>
      <div dangerouslySetInnerHTML={{ __html: TREE_HTML }} />
      <figcaption className="phil-fig-note">
        Bail-out is per component, not per app. One stubborn component runs on
        the fallback; everything around it stays compiled, and state, context
        and events flow across the seam.
      </figcaption>
    </figure>
  );
}

function DeliveryTimeline() {
  return (
    <figure className="phil-tl" aria-label="First-content timeline comparison">
      <p className="phil-fig-title">Where the first content appears</p>
      <div dangerouslySetInnerHTML={{ __html: TIMELINE_HTML }} />
      <figcaption className="phil-fig-note">
        Schematic, not a benchmark: a classic SPA paints after the runtime
        boots; a prerendered JetShake page is content in the first response,
        with a few kilobytes of signals arriving behind it.
      </figcaption>
    </figure>
  );
}

function BuildReportCard() {
  return (
    <figure className="phil-report" aria-label="Build report output">
      <p className="phil-fig-title">This page's build, as reported on stdout</p>
      <pre dangerouslySetInnerHTML={{ __html: REPORT_HTML }} />
      <figcaption className="phil-fig-note">
        Every <code>vite build</code> prints what the engine decided and what it
        cost, so the claims on this page are checkable.
      </figcaption>
    </figure>
  );
}

function CounterFigure() {
  return (
    <figure className="phil-code">
      <span className="phil-code-label">Ordinary React in</span>
      <pre dangerouslySetInnerHTML={{ __html: tokenizeHtml(COUNTER_SRC) }} />
    </figure>
  );
}

function ExportFigure() {
  return (
    <figure className="phil-code">
      <span className="phil-code-label">
        One component, exported for any stack
      </span>
      <pre dangerouslySetInnerHTML={{ __html: tokenizeHtml(EXPORT_SRC) }} />
    </figure>
  );
}

/** Section reveal stagger - same fade-up the how-it-works page uses. */
const SEC_START = 0.7;
const SEC_STEP = 0.18;

export default function Philosophy() {
  const rKicker = revealHostProps(0.35);
  const rTldr = revealHostProps(0.5);
  const r1 = revealHostProps(SEC_START);
  const r2 = revealHostProps(SEC_START + SEC_STEP);
  const r3 = revealHostProps(SEC_START + 2 * SEC_STEP);
  const r4 = revealHostProps(SEC_START + 3 * SEC_STEP);
  const r5 = revealHostProps(SEC_START + 4 * SEC_STEP);
  const r6 = revealHostProps(SEC_START + 5 * SEC_STEP);
  const r7 = revealHostProps(SEC_START + 6 * SEC_STEP);
  const r8 = revealHostProps(SEC_START + 7 * SEC_STEP);
  const r9 = revealHostProps(SEC_START + 8 * SEC_STEP);

  return (
    <>
      <Nav />
      <main className="phil">
        <header className="phil-hero">
          <h1 className="phil-title">
            <span className="phil-title-primary">
              <TypeIn text="Keep the language." delay={0.02} />
            </span>{" "}
            <span className="phil-title-secondary">
              <TypeIn text="Replace the engine." delay={0.18} />
            </span>
          </h1>
          <div
            className={rKicker.className}
            style={rKicker.style}
            onAnimationEnd={rKicker.onAnimationEnd}
          >
            <p className="phil-kicker">The JetShake philosophy.</p>
          </div>
          <div
            className={rTldr.className}
            style={rTldr.style}
            onAnimationEnd={rTldr.onAnimationEnd}
          >
            <p className="phil-tldr">
              <strong>TL;DR:</strong> React won as a language and lost as a
              runtime. JetShake is an optimizing compiler for the React dialect:
              standard JSX and hooks in, signals and direct DOM out, HTML first,
              and a runtime that scales down to what your app actually uses
              instead of up to what the framework might need.
            </p>
          </div>
        </header>

        <section
          className={r1.className}
          style={r1.style}
          onAnimationEnd={r1.onAnimationEnd}
        >
          <h2 className="phil-h">
            <ReactLogo className="phil-react-logo" />
            React is a language now
          </h2>
          <p className="phil-p">
            At some point in the last decade, React stopped being a library and
            became a dialect. Millions of developers think in JSX and hooks.
            Every design system that matters ships in it. Every AI model writes
            it by default when you ask for UI. It's the most widely known way of
            describing an interface, for people and for models, and along the
            way it picked up the largest component ecosystem ever built.
          </p>
          <p className="phil-p">
            And all of it is tied to one delivery mechanism. Using any of it
            means shipping the React runtime: tens of kilobytes of reconciler
            before your first component renders, and a virtual DOM that spends
            the app's whole life working out, at runtime, things that were
            obvious in the source code. <code>{"<h1>{count}</h1>"}</code>{" "}
            doesn't need a diffing engine to discover that only a text node
            changes - that was knowable at build time. React ships the machinery
            to rediscover it on every update anyway.
          </p>
          <p className="phil-p phil-punch">
            That's the tax JetShake exists to remove.
          </p>
        </section>

        <section
          className={r2.className}
          style={r2.style}
          onAnimationEnd={r2.onAnimationEnd}
        >
          <h2 className="phil-h">
            <SvelteLogo className="phil-svelte-logo" />
            <SolidLogo className="phil-solid-logo" />
            What Svelte and Solid proved
          </h2>
          <p className="phil-p">
            Svelte showed that a framework can be a compiler. Solid showed that
            fine-grained signals make the virtual DOM optional. JetShake is
            built directly on those models - a compiler that emits signals.
            Between them they settled the technical argument years ago: compile
            ahead of time, update only what changed, ship less. JetShake doesn't
            reopen that argument. It agrees with it completely.
          </p>
          <p className="phil-p">
            The one thing left unsolved was reach. Both ask you to switch: a new
            dialect, a new ecosystem, a team that has to relearn its tools, and
            today, AI models that write React whatever you ask for. For a
            greenfield project that's often a fair trade, and plenty of teams
            make it happily. But for the millions already fluent in React, the
            cost of leaving has kept a settled idea from ever reaching them.
          </p>
          <p className="phil-p">
            JetShake's bet is that you shouldn't have to give up React to get
            it.
            <br />
            <span className="phil-punch">
              Write the same React code. Compile it to something better.
            </span>
          </p>
        </section>

        <section
          className={r3.className}
          style={r3.style}
          onAnimationEnd={r3.onAnimationEnd}
        >
          <h2 className="phil-h">
            <img
              className="phil-jet-logo"
              src="/jetshake-mark.svg"
              alt=""
              width={20}
              height={20}
            />
            What the compiler does
          </h2>
          <p className="phil-p">
            You write an ordinary React component - <code>useState</code>,
            props, JSX, nothing annotated, nothing renamed:
          </p>
          <CounterFigure />
          <p className="phil-p">
            JetShake compiles it the way Solid or Svelte would have you author
            it: <code>useState</code> lowers to a signal, the JSX lowers to real
            DOM construction, and the <code>{"{count}"}</code> binding becomes a
            targeted text-node update subscribed to exactly one signal. No
            virtual DOM is created, diffed, or shipped. There's no React runtime
            in the output - the emitted contract is just{" "}
            <code>Component(props) -&gt; Node</code>.
          </p>
          <p className="phil-p">
            Because it's a compiler with whole-program visibility, it doesn't
            stop at one component. It sees your app, your imported libraries,
            the interaction patterns between them - context, keyed lists, refs,
            portals, transitions - and lowers what it can prove.
          </p>
        </section>

        <section
          className={r4.className}
          style={r4.style}
          onAnimationEnd={r4.onAnimationEnd}
        >
          <h2 className="phil-h">What you don't use, you don't pay for</h2>
          <p className="phil-p">
            A framework is a fixed cost: every feature any app might ever need,
            shipped to every visitor, mostly unused. C++ has had a rule against
            this since the eighties - Stroustrup's zero-overhead principle, in
            two halves: <strong>what you don't use, you don't pay for</strong>,
            and what you do use, you couldn't hand-code better. Somewhere along
            the way, UI frameworks dropped both halves.
          </p>
          <p className="phil-p">
            Our internal doctrine restates the rule for UI delivery:{" "}
            <strong>
              app mode must not pull features the app does not use.
            </strong>{" "}
            <code>createRoot</code> is a boot, not permission to ship the
            reconciler monolith.
          </p>
          <p className="phil-p">
            So the JetShake runtime is a set of small modules - signal core,
            keyed-list diffing, style application, ref plumbing, context,
            transitions - and each one is emitted only when the compiled output
            actually references it. No keyed <code>.map()</code> in your app, no
            list differ in your bundle.
          </p>
          <MoonGrid />
          <p className="phil-p">
            The result scales down - with what's actually built:
          </p>
          <PayloadLadder />
          <p className="phil-p">
            And the top row is the principle's second half at work: compiled
            output converging on what you'd write by hand. The same code through
            the standard toolchain would start at React's fixed floor and go up.
            That scaling is the real product: you pay for what you built, not
            for what the framework is.
          </p>
          <p className="phil-p">
            There's a falsifiable version of the rule we test against: a whole
            app should weigh about the same as its pieces exported one by one.
            If splitting your app into islands by hand saves bytes, app mode has
            a bug.
          </p>
        </section>

        <section
          className={r5.className}
          style={r5.style}
          onAnimationEnd={r5.onAnimationEnd}
        >
          <h2 className="phil-h">A compiler you don't have to trust blindly</h2>
          <p className="phil-p">
            Compiling a language you don't control means running into code you
            can't fully lower. Every compiler for an existing language faces
            this, and there are two honest responses: restrict the input ("our
            subset of React"), or guarantee the fallback. Restricting the input
            silently recreates the trade we refused - you end up in a dialect
            again, just an undocumented one.
          </p>
          <p className="phil-p">
            So JetShake carries its correctness guarantee inside the engine:{" "}
            <strong>
              the compiler never breaks your component. Anything it can't fully
              optimize still runs correctly - just bigger.
            </strong>{" "}
            When a component defeats analysis, that component - not your app -
            falls back to a React-compatible rendering path, bundled when it's
            reachable and tree-shaken to zero when it isn't.
          </p>
          <MixedTree />
          <p className="phil-p">
            None of this runs on the honour system. React's own unit-test suite
            runs against the engine's compatibility surface. Hundreds of
            differential browser cases render every scenario twice - real React
            beside compiled output - and assert observable equivalence: DOM,
            event behaviour, effect timing. The rule for the test suite fits in
            a sentence:{" "}
            <em>
              a test may only fail when a real React app would behave
              differently.
            </em>{" "}
            Per-library suites (Radix, BaseUI, react-hook-form, TanStack Query,
            Zustand, react-select) run the same way. Ecosystem support is
            something you demonstrate, one library at a time.
          </p>
        </section>

        <section
          className={r6.className}
          style={r6.style}
          onAnimationEnd={r6.onAnimationEnd}
        >
          <h2 className="phil-h">
            <HtmlLogo className="phil-html-logo" />
            HTML is the best delivery format
          </h2>
          <p className="phil-p">
            A compiler that lowers JSX to DOM instructions knows, at build time,
            what the page looks like. It would be negligent not to write that
            down.
          </p>
          <p className="phil-p">
            So the default delivery for a JetShake app is HTML first: the build
            prerenders each route to a static HTML file and ships the
            interactive remainder in a few kilobytes of client JavaScript. First
            paint is a file on a CDN. Search engines and link-unfurlers read
            real markup. The JavaScript comes after, and there's only a few
            kilobytes of it - not a platform the page has to boot before it can
            exist.
          </p>
          <DeliveryTimeline />
          <p className="phil-p">
            This is static generation, not server-side rendering - there's no
            server on the hot path and nothing re-renders per request. For the
            large class of pages whose HTML doesn't depend on the request, doing
            the work once at build time beats redoing it on every request: one
            artifact, cacheable anywhere, no server to keep alive. Request-time
            SSR remains the right tool when HTML genuinely varies per request,
            and it composes with the same engine.
          </p>
          <p className="phil-p">
            If this sounds like islands architecture: it is, with one structural
            difference. Islands frameworks make separate roots that can't share
            context, state or a tree, and make you leave the React dialect (or
            pay React per island) to get them. A JetShake app stays one React
            program - one tree, context and composition intact - and the{" "}
            <em>compiler</em> does the splitting, by reachability, including
            per-route chunks it prerenders and preloads.
          </p>
        </section>

        <section
          className={r7.className}
          style={r7.style}
          onAnimationEnd={r7.onAnimationEnd}
        >
          <h2 className="phil-h">The locked ecosystem, unlocked</h2>
          <p className="phil-p">
            The strangest consequence of treating React as a language is that
            the output doesn't need React on the page - so it doesn't need a
            React app around it either.
          </p>
          <p className="phil-p">
            <code>jetshake export</code> compiles a component into a
            self-contained HTML tag. A Laravel, Rails, Django or plain-HTML page
            includes one script and writes <code>&lt;jet-dialog&gt;</code> -
            data in through attributes, events out as native DOM events, no npm,
            no build step, no runtime dependency on the React project the
            component came from.
          </p>
          <ExportFigure />
          <p className="phil-p">
            In real numbers: a pure-CSS-and-hooks component exports at a few KB;
            heavier, logic-dense components cost more; and a library that's
            deeply coupled to React's runtime lifecycle (animation runtimes,
            notably) falls back to the compatibility path - correct, but not
            small, so we don't market it small.
          </p>
          <p className="phil-p">
            React built the largest UI ecosystem in history and locked it inside
            one runtime. Compile that ecosystem's unmodified source to
            framework-free output, and it opens up to every stack that could
            never justify shipping React - which is most of the web.
          </p>
        </section>

        <section
          className={r8.className}
          style={r8.style}
          onAnimationEnd={r8.onAnimationEnd}
        >
          <h2 className="phil-h">The best build is the default</h2>
          <p className="phil-p">
            Most tools make you earn good output. You learn the right flags, the
            right config, the right plugin order, and you're rewarded with a
            smaller bundle - miss one and you quietly ship the slow version.
            JetShake turns that around: the best build is the one you get by
            typing nothing. Flags exist only to make the output <em>worse</em> -
            to override the engine on the rare occasion you know something it
            doesn't.
          </p>
          <p className="phil-p">
            You should never have to become a packaging architect. Whether your
            code is one app, a group of components sharing a store, or a single
            exported tag - and whether it prerenders, splits, or inlines - the
            engine decides from the shape of your code, and tells you what it
            chose on stdout. The whole workflow is: run the build, read what it
            did, ship. If you ever go hunting for a flag to get the good output,
            that's a bug in the defaults, not a lesson you should have learned.
          </p>
          <BuildReportCard />
        </section>

        <section
          className={r9.className}
          style={r9.style}
          onAnimationEnd={r9.onAnimationEnd}
        >
          <h2 className="phil-h">
            <img
              className="phil-jet-logo"
              src="/jetshake-mark.svg"
              alt=""
              width={20}
              height={20}
            />
            Trust is the product
          </h2>
          <p className="phil-p">
            A compiler that rewrites other people's components lives or dies on
            trust, so the strategy is deliberately unexciting: verify against
            React itself, publish numbers that reproduce, and don't claim more
            than the current tier can prove. There's a dogfooding rule too: any
            page that says "no React runtime" has to be compiled by JetShake,
            and this one is - you can check the network tab. A project like this
            doesn't get to fake a demo.
          </p>
          <p className="phil-p">
            The endgame isn't a framework with a logo and a conference. It's
            quieter than that: you keep writing the React you already write, and
            the delivery layer - runtime, reconciler, hydration waterfall, the
            fixed tax on every visitor - just stops being something anyone
            thinks about.
          </p>
        </section>

        <Closing
          delay={SEC_START + 9 * SEC_STEP}
          lines={[
            { text: "Write React.", tone: "fg" },
            { text: "Ship the Web.", tone: "muted" },
          ]}
        />
      </main>
    </>
  );
}
